import {
  Box,
  Checkbox,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { useContext } from "react";
import {
  BentoCategory,
  OrganizationNiche,
  TemplateIntakeForm,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { makeDeepCopy } from "utils/updateLocalState";

import styles from "../styles";
import PickerDropdown from "./Picker";

interface Props {
  niche: OrganizationNiche;
  niches: OrganizationNiche[];
  setNiches: React.Dispatch<React.SetStateAction<OrganizationNiche[]>>;
  index: number;
  categoriesToPickFrom: BentoCategory[];
  templateIntakeForms?: TemplateIntakeForm[];
}

const Niche = ({
  niche,
  niches,
  setNiches,
  index,
  categoriesToPickFrom,
  templateIntakeForms,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const onChange = (e: CustomEvent) => {
    const { value, name, checked } = e.target;
    const updatedNiches = makeDeepCopy(niches);
    if (name === "needsTemplate") {
      const totalNeedsTemplateSoFar = niches.filter(
        (niche) => niche.needsTemplate,
      ).length;
      if (
        checked &&
        totalNeedsTemplateSoFar >= (templateIntakeForms?.length || 0)
      ) {
        setAlert(
          `You only paid for ${templateIntakeForms?.length || 0} custom templates`,
          "warning",
        );
        return;
      }
      updatedNiches[index] = {
        ...updatedNiches[index],
        [name]: checked,
      };
      setNiches(updatedNiches);
      return;
    }

    if (name === "bentoCategoryId") {
      const exists = updatedNiches.some(
        (niche: OrganizationNiche) => niche?.bentoCategoryId === value,
      );
      if (exists) {
        setAlert("You already picked this niche", "warning");
        return;
      }
      const nicheName = categoriesToPickFrom.find(
        (category) => category.id === value,
      )?.name;
      updatedNiches[index] = {
        ...updatedNiches[index],
        [name]: value,
        name: nicheName || "",
      };
      setNiches(updatedNiches);
      return;
    }
    updatedNiches[index] = {
      ...updatedNiches[index],
      [name]: value,
    };
    setNiches(updatedNiches);
  };

  return (
    <Grid container alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="h6">
        <strong>Niche {index + 1}</strong>
      </Typography>
      <Grid item xs={12}>
        <FormHelperText>Pick a niche below *</FormHelperText>
        <PickerDropdown
          categoriesToPickFrom={categoriesToPickFrom}
          niche={niche}
          onChange={onChange}
        />
        <FormHelperText>
          Include <strong>ALL details</strong> we will need to pick fitting
          brands for you *
        </FormHelperText>
        <TextField
          sx={styles.textField}
          fullWidth
          name="notes"
          placeholder={
            "For example, how old your kids are; what kind of pet you have; or where in the world you would like to travel"
          }
          onChange={onChange}
          required
          value={niches[index]?.notes || ""}
        />
        {templateIntakeForms && templateIntakeForms?.length > 0 && (
          <>
            <Checkbox
              name="needsTemplate"
              checked={Boolean(niche.needsTemplate)}
              onChange={onChange}
              sx={{ ml: -1 }}
              icon={<Box component="i" className="fa-regular fa-square" />}
              checkedIcon={
                <Box component="i" className="fa-solid fa-check-square" />
              }
            />
            Write a custom outreach template for this niche
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default Niche;
